import React, {useState, useEffect, useRef} from 'react';
import {RadioGroup} from '@headlessui/react';
import {useField, useFormikContext} from 'formik';
import {CloudUploadIcon} from '@heroicons/react/outline';

import DropdownInput from './DropdownInput';
import ClickContainer from './ClickContainer';
import DropDownMultiSelect from './DropDownMultiSelect';
import NonFormStarRating from './StarRating';
import {cx} from '../util';

export const StyledTextInput = React.forwardRef(({className, ...props}, ref) => (
  <input
    className={cx('text-sm rounded-lg border border-gray-300 bg-white hover:border-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-3 py-2 w-full focus:outline-none text-gray-800 placeholder-gray-400', className)}
    {...props}
    ref={ref}
  />
));

export const StyledTextArea = React.forwardRef(({className, ...props}, ref) => (
  <textarea
    className={cx('text-sm rounded-lg border border-gray-300 bg-white hover:border-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-3 py-2 w-full focus:outline-none text-gray-800 placeholder-gray-400 min-h-[80px]', className)}
    {...props}
    ref={ref}
  />
));

export const StyledSelect = React.forwardRef(({className, ...props}, ref) => (
  <select
    className={cx('text-sm rounded-lg border border-gray-300 bg-white hover:border-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-3 py-2 w-full focus:outline-none text-gray-800', className)}
    {...props}
    ref={ref}
  />
));

// TODO: Decouple this from Formik
export const StyledRadioButtons = React.forwardRef(({className, ...props}, ref) => (
  <div className="flex justify-items-start" ref={ref}>
    {props.options.map((option) => (
      <div className="mr-4">
        <label className="flex flex-col items-center select-none">
          <input
            type="radio"
            name={props.name}
            value={option.value}
            onClick={() => props.setValue(option.value)}
            className="opacity-0 h-0 w-0"
          />
          <div
            className={cx(
              'w-4 h-4 mb-2 inline-block shadow-sm rounded-full transition focus:ring-2',
              {'border border-gray-300': props.value !== option.value},
              {'ring-2 ring-indigo-300 bg-indigo-500 border-2 border-white': props.value === option.value},
            )}
          />
          <div>
            {option.label}
          </div>
        </label>
      </div>
    ))}
  </div>
));

// TODO: Decouple this from Formik
export const StyledRadioCardButtons = ({
  options, value, setValue, className,
}) => (
  <RadioGroup value={value} onChange={setValue} className={cx('mt-2 grid grid-cols-3 gap-3 sm:grid-cols-6', className)}>
    {options.map((option) => (
      <RadioGroup.Option
        key={option.value}
        value={option.value}
        className={cx(
          'flex items-center justify-center rounded-md bg-white px-3 py-3 text-sm font-semibold ring-1 hover:bg-gray-50 focus:ring-2 outline-none cursor-pointer sm:flex-1',
          {'text-gray-900 ring-gray-300 focus:ring-indigo': value !== option.value},
          {'text-indigo ring-indigo focus:ring-indigo': value === option.value},
        )}
      >
        {option.label}
      </RadioGroup.Option>
    ))}
  </RadioGroup>
);

export const StyledCheckbox = React.forwardRef(({
  className, checked, onChange, ...props
}, ref) => (
  <input
    type="checkbox"
    className={cx('form-checkbox focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 my-1 h-4 w-4 text-indigo-600 border-gray-300 rounded', className)}
    checked={checked}
    onChange={onChange}
    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
    {...props}
    ref={ref}
  />
));

export const StyledLabel = ({htmlFor, className, children}) => (
  <label
    htmlFor={htmlFor}
    className={cx('text-sm font-medium text-gray-600 tracking-tight leading-normal text-left cursor-pointer mb-2', className)}
  >
    {children}
  </label>
);

export const StyledFileInput = React.forwardRef(({className, ...props}, ref) => (
  <input
    type="file"
    className={cx('text-base rounded-lg shadow-sm border border-gray-300 focus:border-gray-400 px-3 py-2 focus:outline-none appearance-none', className)}
    {...props}
    ref={ref}
    value="" // Needed to disallow Formik from setting value for some reason https://stackoverflow.com/q/66876022/3681279
  />
));

export const LabelAndErrors = ({
  label,
  children,
  required,
  className,
  suppressRequiredStar = false,
  suppressErrorMessage = false, // In case this is wrapping children that already display an error
  ...props
}) => {
  // eslint-disable-next-line no-unused-vars
  const [field, meta] = useField(props);
  const fieldId = props.id || field.id || field.name;
  return (
    <div className={cx('w-full flex flex-col mb-4', className)}>
      {label && (
        <label htmlFor={fieldId} className="block text-sm font-medium text-gray-600 tracking-tight leading-normal text-left mb-2">
          {label}
          {required && !suppressRequiredStar && (
            <span className="text-red-600" aria-label="Required">&nbsp;*</span>
          )}
        </label>
      )}
      {children}
      {meta.touched && meta.error && !suppressErrorMessage
        ? <div className="text-red-600 text-xs text-left mt-1">{meta.error}</div>
        : null}
    </div>
  );
};

export const TextInput = ({
  required, onBlur, onChange, ...props
}) => {
  const [field] = useField(props);
  const handleBlur = (e) => {
    field.onBlur(e);
    if (onBlur) onBlur(e);
  };
  const handleChange = (e) => {
    if (onChange) onChange(e);
    field.onChange(e);
  };
  return (
    <LabelAndErrors required={required} {...props}>
      <StyledTextInput
        id={props.id || field.name}
        required={required}
        {...field}
        {...props}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </LabelAndErrors>
  );
};

export const TextArea = ({required, onBlur, ...props}) => {
  const [field] = useField(props);
  const handleBlur = (e) => {
    field.onBlur(e);
    if (onBlur) onBlur(e);
  };
  return (
    <LabelAndErrors required={required} {...props}>
      <StyledTextArea
        id={props.id || field.name}
        required={required}
        {...field}
        {...props}
        onBlur={handleBlur}
      />
    </LabelAndErrors>
  );
};

export const Select = ({
  required, options, placeholder, onChange, children, ...props
}) => {
  const [field, , helpers] = useField(props);
  const handleChange = (e) => {
    field.onChange(e);
    helpers.setTouched(true);
    if (onChange) onChange(e.target.value);
  };
  return (
    <LabelAndErrors required={required} {...props}>
      <StyledSelect
        id={props.id || field.name}
        required={required}
        {...field}
        {...props}
        onChange={handleChange}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options?.map(({value, label: optLabel}) => (
          <option key={value} value={value}>{optLabel}</option>
        ))}
        {children}
      </StyledSelect>
    </LabelAndErrors>
  );
};

export const RadioButtons = ({required, ...props}) => {
  const [field, meta, helpers] = useField(props);
  return (
    <LabelAndErrors required={required} {...props}>
      <StyledRadioButtons setValue={helpers.setValue} {...field} {...props} />
    </LabelAndErrors>
  );
};

export const RadioCardButtons = ({required, ...props}) => {
  const [field, meta, helpers] = useField(props);
  return (
    <LabelAndErrors required={required} {...props}>
      <StyledRadioCardButtons setValue={helpers.setValue} {...field} {...props} />
    </LabelAndErrors>
  );
};


export const Checkbox = ({
  label, className, onChange, ...props
}) => {
  const [field] = useField({...props, type: 'checkbox'});
  const handleChange = (e) => {
    field.onChange(e);
    if (onChange) onChange(e);
  };
  return (
    <LabelAndErrors className={className} {...props}>
      <label className="flex items-start text-sm font-medium text-gray-600 tracking-tight leading-normal text-left cursor-pointer">
        {/* value falls back to "true" so Formik coerces to a boolean even when the
            field is missing from initialValues. React 19 no longer writes the value
            property for value={undefined}, leaving the browser default "on", which
            makes Formik store ["on"] instead of true. See formik#4017. */}
        <StyledCheckbox {...field} {...props} value={props.value ?? 'true'} onChange={handleChange} />
        <span className="ml-3">{label}</span>
      </label>
    </LabelAndErrors>
  );
};

export const StarRating = ({
  label, name, required, ...props
}) => {
  const [field, meta, helpers] = useField(name);

  return (
    <LabelAndErrors label={label} name={name} required={required}>
      <NonFormStarRating {...props} onRating={helpers.setValue} />
    </LabelAndErrors>
  );
};

export const FileInput = ({required, ...props}) => {
  const [field, meta, helpers] = useField(props);
  const {value} = meta;
  const {setValue} = helpers;
  const filenameId = `${field.name}-filename`;
  const inputId = props.id || field.name;

  return (
    <LabelAndErrors required={required} {...props}>
      {value ? (
        <div className="flex">
          <span id={filenameId} className="text-sm md:text-base font-medium leading-tight text-gray-600" aria-live="polite">
            {value.name}
          </span>
          <button
            type="button"
            onClick={() => setValue(undefined)}
            className="ml-4 text-sm md:text-base font-medium leading-tight text-indigo"
            aria-labelledby={filenameId}
            aria-label={`Remove file ${value.name}`}
          >
            Remove
          </button>
        </div>
      ) : (
        <StyledFileInput id={inputId} required={required} {...field} {...props} />
      )}
    </LabelAndErrors>
  );
};

export const DropFileInput = ({required, ...props}) => {
  const [field, meta, helpers] = useField(props);
  const {value} = meta;
  const {setValue} = helpers;
  const inputRef = React.useRef(null);
  const inputId = props.id || field.name || 'input-file-upload';
  const filenameId = `${inputId}-filename`;

  const [fileError, setFileError] = React.useState(false);
  // drag state
  const [dragActive, setDragActive] = React.useState(false);

  // handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // verify file is in correct format
      if (e.dataTransfer.files[0].type !== props.accept) {
        setFileError(true);
        return;
      }
      setFileError(false);
      setValue(e.dataTransfer.files[0]);
    }
  };

  // triggers the input when the button is clicked
  const onButtonClick = () => {
    inputRef.current.click();
  };

  return (
    <LabelAndErrors required={required} {...props}>
      {value ? (
        <div className="flex">
          <span id={filenameId} className="text-sm md:text-base font-medium leading-tight text-gray-600" aria-live="polite">
            {value.name}
          </span>
          <button
            type="button"
            onClick={() => setValue(undefined)}
            className="ml-4 text-sm md:text-base font-medium leading-tight text-indigo"
            aria-labelledby={filenameId}
            aria-label={`Remove file ${value.name}`}
          >
            Remove
          </button>
        </div>
      ) : (
        <div onDragEnter={handleDrag} className={cx(dragActive && 'bg-gray-200 border-gray-400', 'flex justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-gray-400 focus:outline-none')}>
          <StyledFileInput id={inputId} required={required} {...field} {...props} className="hidden" ref={inputRef} />
          <label className="flex items-center justify-center space-x-2" htmlFor={inputId}>
            <CloudUploadIcon className="h-6 w-6 text-gray-600 mr-1" />
            {!dragActive ? (
              <span className="font-medium text-gray-600">
                {fileError && (<span className="font-semibold text-red-600">Incorrect file type. </span>)}
                Drop files to attach, or
                {' '}
                <button
                  type="button"
                  className="text-blue-600 underline"
                  onClick={onButtonClick}
                >
                  browse
                </button>
              </span>
            ) : (<span className="font-medium text-gray-600">Drop here</span>)}
          </label>
          { dragActive && (
          <div
            className="absolute w-full h-full rounded-lg top-0 right-0 bottom-0 left-0"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          />
          )}
        </div>
      )}
    </LabelAndErrors>
  );
};

export const InputWithSuggestions = ({
  updateSuggestions,
  required,
  suggestions = [],
  open: initialOpen = false,
  onBlur,
  strictSelection = false,
  multiSelect = false,
  ...props
}) => {
  const [open, setOpen] = useState(initialOpen);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [field] = useField(props);
  const {setFieldValue} = useFormikContext();

  // Try to find matching item from suggestions when value changes externally
  useEffect(() => {
    if (multiSelect) {
      // For multiSelect, field.value should be an array of names
      if (Array.isArray(field.value) && field.value.length > 0 && selectedItems.length === 0) {
        const matchedItems = field.value
          .map((name) => suggestions.find(({item}) => item.name === name)?.item)
          .filter(Boolean);
        if (matchedItems.length > 0) {
          setSelectedItems(matchedItems);
        }
      }
    } else if (field.value && suggestions.length > 0 && !selectedItem) {
      const match = suggestions.find(({item}) => item.name === field.value);
      if (match) {
        setSelectedItem(match.item);
      }
    }
  }, [field.value, suggestions, selectedItem, selectedItems.length, multiSelect]);

  const scrollToIndex = (index) => {
    if (index >= 0 && listRef.current) {
      const item = listRef.current.querySelector(`#suggestion-${index}`);
      if (item) {
        item.scrollIntoView({block: 'nearest'});
      }
    }
  };

  const onChange = (e) => {
    setOpen(true);
    setHighlightedIndex(-1);
    if (multiSelect) {
      setInputValue(e.target.value);
      updateSuggestions(e.target.value);
    } else {
      setSelectedItem(null); // Clear selected item when typing
      updateSuggestions(e.target.value);
      field.onChange(e);
    }
  };

  const onSelectSuggestion = (item) => {
    setHighlightedIndex(-1);

    if (multiSelect) {
      // Toggle selection
      const isAlreadySelected = selectedItems.some((selected) => selected.id === item.id);
      let newSelectedItems;
      if (isAlreadySelected) {
        newSelectedItems = selectedItems.filter((selected) => selected.id !== item.id);
      } else {
        newSelectedItems = [...selectedItems, item];
      }
      setSelectedItems(newSelectedItems);
      setInputValue('');
      updateSuggestions('');
      setFieldValue(field.name, newSelectedItems.map((i) => i.name));
      inputRef.current?.focus();
    } else {
      setOpen(false);
      setSelectedItem(item);
      updateSuggestions(item.name);
      setFieldValue(field.name, item.name);
      if (onBlur) onBlur({target: {value: item.name}});
    }
  };

  const removeSelectedItem = (itemToRemove) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemToRemove.id);
    setSelectedItems(newSelectedItems);
    setFieldValue(field.name, newSelectedItems.map((i) => i.name));
    inputRef.current?.focus();
  };

  const handleBlur = (e) => {
    // Don't close if focus is moving within the container (e.g., clicking remove button)
    if (containerRef.current?.contains(e.relatedTarget)) {
      return;
    }

    setOpen(false);
    setHighlightedIndex(-1);

    if (strictSelection && !multiSelect) {
      // In strict mode, revert to last valid selection if current value doesn't match
      const currentValue = e.target.value;
      const matchingSuggestion = suggestions.find(({item}) => item.name === currentValue);

      if (!matchingSuggestion && currentValue) {
        // Revert to previous valid selection or clear
        if (selectedItem) {
          setFieldValue(field.name, selectedItem.name);
        } else {
          setFieldValue(field.name, '');
        }
      }
    }

    if (multiSelect) {
      setInputValue('');
    }

    if (onBlur) onBlur(e);
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'Backspace':
        // Remove last selected item in multiSelect mode when input is empty
        if (multiSelect && inputValue === '' && selectedItems.length > 0) {
          const lastItem = selectedItems[selectedItems.length - 1];
          removeSelectedItem(lastItem);
        }
        break;
      case 'ArrowDown': {
        if (!open || suggestions.length === 0) return; // Only relevant if dropdown is open

        e.preventDefault();
        const newIndex = highlightedIndex < suggestions.length - 1 ? highlightedIndex + 1 : 0;
        setHighlightedIndex(newIndex);
        scrollToIndex(newIndex);
        break;
      }
      case 'ArrowUp': {
        if (!open || suggestions.length === 0) return; // Only relevant if dropdown is open

        e.preventDefault();
        const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : suggestions.length - 1;
        setHighlightedIndex(newIndex);
        scrollToIndex(newIndex);
        break;
      }
      case 'Escape':
        if (!open || suggestions.length === 0) return; // Only relevant if dropdown is open

        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          onSelectSuggestion(suggestions[highlightedIndex].item);
        }
        break;
      default:
        break;
    }
  };

  // Needed because: https://stackoverflow.com/a/57630197/3681279
  const preventDefault = (e) => e.preventDefault();

  // Multiselect mode rendering
  if (multiSelect) {
    return (
      <LabelAndErrors required={required} {...props}>
        <ClickContainer onClose={open && suggestions.length > 0 ? () => setOpen(false) : null}>
          <div className="relative" ref={containerRef}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
              role="group"
              className={cx(
                'flex flex-wrap items-center gap-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:border-gray-400 focus-within:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 px-2 py-1.5 w-full',
              )}
              onClick={() => inputRef.current?.focus()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') inputRef.current?.focus();
              }}
            >
              {selectedItems.map((item) => (
                <span
                  key={item.id}
                  className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded"
                >
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt=""
                      className="h-3 w-3 object-contain"
                    />
                  )}
                  {item.name}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(item);
                    }}
                    className="text-indigo-600 hover:text-indigo-800 focus:outline-none"
                    aria-label={`Remove ${item.name}`}
                  >
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={onChange}
                onBlur={handleBlur}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                className="flex-1 min-w-[120px] border-0 p-0.5 focus:outline-none focus:ring-0 text-sm text-gray-800 placeholder-gray-400"
                placeholder={selectedItems.length === 0 ? props.placeholder : ''}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={initialOpen}
                autoComplete="off"
                role="combobox"
                aria-controls="suggestions-listbox"
                aria-expanded={open && suggestions.length > 0}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-activedescendant={
                  highlightedIndex >= 0 ? `suggestion-${highlightedIndex}` : undefined
                }
              />
            </div>
            <ul
              ref={listRef}
              className={cx(
                'absolute left-0 right-0 border border-gray-200 shadow-lg rounded-lg z-10 overflow-hidden max-h-48 bg-white',
                open && suggestions.length > 0 ? 'visible' : 'invisible',
              )}
              style={{top: '100%', marginTop: 4}}
              role="listbox"
            >
              <div className="flex flex-col bg-white overflow-auto max-h-48 scrollbar-hidden">
                {suggestions.map(({item}, index) => {
                  const isSelected = selectedItems.some((selected) => selected.id === item.id);
                  return (
                    <li key={item.id}>
                      <button
                        id={`suggestion-${index}`}
                        type="button"
                        onMouseDown={preventDefault}
                        onClick={() => onSelectSuggestion(item)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={cx(
                          'w-full py-3 px-5 cursor-pointer text-sm text-left flex items-center hover:bg-gray-50',
                          isSelected && 'text-indigo-700',
                        )}
                        role="option"
                        aria-selected={isSelected}
                      >
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt={`${item.name} logo`}
                            className="h-3.5 w-3.5 mr-2 object-contain"
                          />
                        )}
                        {item.name}
                        {isSelected && (
                          <svg className="ml-auto h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </li>
                  );
                })}
              </div>
            </ul>
          </div>
        </ClickContainer>
      </LabelAndErrors>
    );
  }

  // Single select mode rendering (original behavior)
  return (
    <LabelAndErrors required={required} {...props}>
      <ClickContainer onClose={open && suggestions.length > 0 ? () => setOpen(false) : null}>
        <div className="relative" ref={containerRef}>
          <div className="relative">
            {selectedItem?.image_url && (
              <img
                src={selectedItem.image_url}
                alt=""
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 object-contain"
              />
            )}
            <StyledTextInput
              {...field}
              {...props}
              ref={inputRef}
              className={cx('text-sm', {'pl-8': selectedItem?.image_url})}
              onChange={onChange}
              onBlur={handleBlur}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              autoFocus={initialOpen}
              autoComplete="off"
              role="combobox"
              aria-expanded={open && suggestions.length > 0}
              aria-haspopup="listbox"
              aria-autocomplete="list"
              aria-activedescendant={
                highlightedIndex >= 0 ? `suggestion-${highlightedIndex}` : undefined
              }
            />
          </div>
          <ul
            ref={listRef}
            className={cx(
              'absolute left-0 right-0 border border-gray-200 shadow-lg rounded-lg z-10 overflow-hidden max-h-48 bg-white',
              open && suggestions.length > 0 ? 'visible' : 'invisible',
            )}
            style={{top: '100%', marginTop: 4}}
            role="listbox"
          >
            <div className="flex flex-col bg-white overflow-auto max-h-48 scrollbar-hidden">
              {suggestions.map(({item}, index) => (
                <li key={item.id}>
                  <button
                    id={`suggestion-${index}`}
                    type="button"
                    onMouseDown={preventDefault}
                    onClick={() => onSelectSuggestion(item)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cx(
                      'w-full py-3 px-5 cursor-pointer text-sm text-left flex items-center',
                      highlightedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50',
                    )}
                    role="option"
                    aria-selected={highlightedIndex === index}
                  >
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={`${item.name} logo`}
                        className="h-3.5 w-3.5 mr-2 object-contain"
                      />
                    )}
                    {item.name}
                  </button>
                </li>
              ))}
            </div>
          </ul>
        </div>
      </ClickContainer>
    </LabelAndErrors>
  );
};

export const FormikDropdownInput = ({
  name, label, required, onSelect, ...props
}) => {
  const [, {value}, {setValue}] = useField(name);

  const handleChange = (newValue) => {
    setValue(newValue);
    if (onSelect) onSelect(newValue);
  };

  return (
    <LabelAndErrors name={name} label={label} required={required}>
      <DropdownInput
        id={name}
        ariaLabel={label}
        value={value}
        onChange={handleChange}
        showReset={false}
        highlightActive={false}
        {...props}
      />
    </LabelAndErrors>
  );
};

export const FormikDropDownMultiSelect = ({
  name, label, options: initialOptions, placeHolder, required, ...props
}) => {
  const [, {value}, {setValue}] = useField(name);
  const [options, setOptions] = useState([]);

  const initializeOptions = (opts, selectedValues) => opts.map((option) => ({
    ...option,
    selected: selectedValues.includes(option.value),
  }));

  useEffect(() => {
    setOptions(initializeOptions(initialOptions, value));
  }, [initialOptions, value]);

  const onChange = (option) => {
    // Toggle the selected state of the option
    const updatedOptions = options.map((o) => (o.value === option.value
      ? {...o, selected: !o.selected} : o));

    // Update the options state
    setOptions(updatedOptions);

    // Update the value array based on the selected state
    const updatedValue = updatedOptions
      .filter((o) => o.selected)
      .map((o) => o.value);

    setValue(updatedValue);
  };

  return (
    <LabelAndErrors name={name} label={label} required={required}>
      <DropDownMultiSelect
        options={options}
        placeholderText={placeHolder || 'Select'}
        onChange={onChange}
        {...props}
      />
    </LabelAndErrors>
  );
};

export const NPS = ({
  name, label, options, required,
}) => {
  const [field, meta, helpers] = useField(name);

  return (
    <LabelAndErrors name={name} label={label} required={required}>
      <RadioGroup value={meta.value} onChange={helpers.setValue} className="mt-2 flex gap-3">
        {options.map((option) => (
          <RadioGroup.Option
            key={option.value}
            value={option.value}
            className={cx(
              'flex items-center justify-center rounded-md bg-white px-3 py-3 text-sm font-semibold ring-1 hover:bg-gray-50 focus:ring-2 outline-none cursor-pointer sm:flex-1',
              {'text-gray-900 ring-gray-300 focus:ring-indigo': meta.value !== option.value},
              {'text-indigo ring-indigo focus:ring-indigo': meta.value === option.value},
            )}
          >
            {option.label}
          </RadioGroup.Option>
        ))}
      </RadioGroup>
      <div className="mt-3 flex justify-between text-sm font-medium leading-tight text-gray-600">
        <div>Not at all likely</div>
        <div>Extremely likely</div>
      </div>
    </LabelAndErrors>
  );
};
